import { useCallback, useEffect, useRef, useState } from 'react';

import type { iMap } from '../../../components/type';
import {
    getInsightsCollectionLinks,
    displayInaccessibleCollectionLinks,
    type InsightsCollectionLinksResult,
    type InsightsCollectionLinkRow,
    type InsightsInaccessibleCollectionLink,
} from '../../../services/api/collectDataService';
import {
    mergeCollectionSelections,
    type SelectedCollectCollection,
} from '../../../utils/analysisContextLinks';

export type MapLinkedCollections = Record<string, string[]>;

export function useInsightsCollectionLinks({
    accessToken,
    selectedMaps,
    selectedCollections,
    onCollectionsChange,
    enabled = true,
}: {
    accessToken: string;
    selectedMaps: iMap[];
    selectedCollections: SelectedCollectCollection[];
    onCollectionsChange: (collections: SelectedCollectCollection[] | null) => void;
    enabled?: boolean;
}) {
    const [inaccessibleLinks, setInaccessibleLinks] = useState<InsightsInaccessibleCollectionLink[]>([]);
    const [linkedCollectionsByMapId, setLinkedCollectionsByMapId] = useState<MapLinkedCollections>({});
    const [isResolvingLinks, setIsResolvingLinks] = useState(false);
    const prevMapIdsRef = useRef<string[]>([]);
    const selectedCollectionsRef = useRef(selectedCollections);

    selectedCollectionsRef.current = selectedCollections;

    const applyLinkResolution = useCallback((result: InsightsCollectionLinksResult) => {
        setInaccessibleLinks(displayInaccessibleCollectionLinks(result));

        const byMap: MapLinkedCollections = {};
        for (const row of result.manageable) {
            for (const mapId of row.linked_map_ids || []) {
                if (!byMap[mapId]) byMap[mapId] = [];
                if (!byMap[mapId].includes(row.id)) {
                    byMap[mapId].push(row.id);
                }
            }
        }
        setLinkedCollectionsByMapId(byMap);
    }, []);

    const syncCollectionsFromLinks = useCallback(
        (manageable: InsightsCollectionLinkRow[], mapsWereAdded: boolean) => {
            if (!mapsWereAdded) return;

            const autoAdd = manageable.filter(
                (c) => c.has_responses && !selectedCollectionsRef.current.some((s) => s.id === c.id),
            );
            if (!autoAdd.length) return;

            onCollectionsChange(
                mergeCollectionSelections(selectedCollectionsRef.current, autoAdd),
            );
        },
        [onCollectionsChange],
    );

    useEffect(() => {
        if (!enabled || !accessToken) {
            setInaccessibleLinks([]);
            setLinkedCollectionsByMapId({});
            return;
        }

        const mapIds = selectedMaps.map((m) => m._id).filter(Boolean);
        const prevMapIds = prevMapIdsRef.current;
        const mapsWereAdded = mapIds.some((id) => !prevMapIds.includes(id));
        prevMapIdsRef.current = mapIds;

        if (!mapIds.length) {
            setInaccessibleLinks([]);
            setLinkedCollectionsByMapId({});
            return;
        }

        let cancelled = false;
        setIsResolvingLinks(true);

        void getInsightsCollectionLinks(mapIds, accessToken)
            .then((result) => {
                if (cancelled) return;
                applyLinkResolution(result);
                syncCollectionsFromLinks(result.manageable, mapsWereAdded);
            })
            .catch(() => {
                if (cancelled) return;
                setInaccessibleLinks([]);
                setLinkedCollectionsByMapId({});
            })
            .finally(() => {
                if (!cancelled) setIsResolvingLinks(false);
            });

        return () => {
            cancelled = true;
        };
    }, [accessToken, selectedMaps, enabled, applyLinkResolution, syncCollectionsFromLinks]);

    return {
        inaccessibleLinks,
        linkedCollectionsByMapId,
        isResolvingLinks,
        displayInaccessibleLinks: inaccessibleLinks,
    };
}
